function pickMotivation(entry) {
  if (!entry) return '';
  const list = Array.isArray(entry.motivations)
    ? entry.motivations.filter(Boolean)
    : entry.motivation
      ? [entry.motivation]
      : [];
  if (list.length === 0) return entry.title || '';
  return list[Math.floor(Math.random() * list.length)];
}

function withMotivation(base, entry) {
  return {
    ...base,
    motivation: pickMotivation(entry),
  };
}

export function resolveClientNextAction({
  t,
  clientsLink,
  displayReferrals = [],
  availableRewards = [],
  hasActiveClaim = false,
  shortActions = [],
  hideRewardsShop = false,
  hideGiftsInTransit = false,
}) {
  const n = t.ux?.nextBestAction;
  if (!n) return null;

  const redeemable = hideRewardsShop
    ? null
    : availableRewards.find((reward) => reward.can_redeem && !reward.has_active_claim);

  if (redeemable) {
    return withMotivation(
      {
        id: 'claim-reward',
        icon: 'gift',
        title: n.clientClaim.title,
        description: n.clientClaim.description,
        cta: n.clientClaim.cta,
        meta: redeemable.name,
        actionType: 'claim',
        rewardId: redeemable.id,
      },
      n.clientClaim
    );
  }

  if (!hideRewardsShop && !hideGiftsInTransit && hasActiveClaim) {
    return withMotivation(
      {
        id: 'track-gift',
        icon: 'truck',
        title: n.clientTrackGift.title,
        description: n.clientTrackGift.description,
        cta: n.clientTrackGift.cta,
        actionType: 'navigate',
        target: 'giftsInTransit',
      },
      n.clientTrackGift
    );
  }

  const topShortAction = shortActions.find((action) => !action.completed);
  if (topShortAction) {
    const shortCopy = n.clientShortAction || n.vendorShortAction;
    return withMotivation(
      {
        id: `short-${topShortAction.action_key}-${topShortAction.entity_id ?? 'daily'}`,
        icon: 'bolt',
        title: topShortAction.title || shortCopy.title,
        description: topShortAction.description || shortCopy.description,
        cta: shortCopy.cta,
        actionType: 'shortAction',
        shortActionKey: topShortAction.action_key,
        shortActionEntityId: topShortAction.entity_id ?? null,
      },
      topShortAction.title
        ? { motivations: [topShortAction.title] }
        : shortCopy
    );
  }

  if (displayReferrals.length === 0 && clientsLink) {
    return withMotivation(
      {
        id: 'invite-first',
        icon: 'users',
        title: n.clientInvite.title,
        description: n.clientInvite.description,
        cta: n.clientInvite.cta,
        actionType: 'navigate',
        target: 'referrals',
      },
      n.clientInvite
    );
  }

  if (clientsLink) {
    return withMotivation(
      {
        id: 'invite-more',
        icon: 'link',
        title: n.clientInviteMore.title,
        description: n.clientInviteMore.description,
        cta: n.clientInviteMore.cta,
        actionType: 'navigate',
        target: 'referrals',
      },
      n.clientInviteMore
    );
  }

  if (hideRewardsShop) {
    return null;
  }

  return withMotivation(
    {
      id: 'browse-rewards',
      icon: 'gift',
      title: n.clientBrowse.title,
      description: n.clientBrowse.description,
      cta: n.clientBrowse.cta,
      actionType: 'navigate',
      target: 'rewards',
    },
    n.clientBrowse
  );
}

export function resolveVendorNextAction({
  t,
  pendingGiftTotal = 0,
  clientCount = 0,
  shortActions = [],
  hidePendingGifts = false,
}) {
  const n = t.ux?.nextBestAction;
  if (!n) return null;

  if (!hidePendingGifts && pendingGiftTotal > 0) {
    return withMotivation(
      {
        id: 'deliver-gifts',
        icon: 'gift',
        title: n.vendorGifts.title,
        description: n.vendorGifts.description,
        cta: n.vendorGifts.cta,
        meta: String(pendingGiftTotal),
        actionType: 'navigate',
        target: 'pendingGifts',
      },
      n.vendorGifts
    );
  }

  const topAction = shortActions.find((action) => !action.completed);
  if (topAction) {
    return withMotivation(
      {
        id: `short-${topAction.action_key}-${topAction.entity_id ?? 'daily'}`,
        icon: 'bolt',
        title: topAction.title || n.vendorShortAction.title,
        description: topAction.description || n.vendorShortAction.description,
        cta: n.vendorShortAction.cta,
        actionType: 'shortAction',
        shortActionKey: topAction.action_key,
        shortActionEntityId: topAction.entity_id ?? null,
      },
      topAction.title
        ? { motivations: [topAction.title] }
        : n.vendorShortAction
    );
  }

  return withMotivation(
    {
      id: 'create-prospect',
      icon: 'user-plus',
      title: n.vendorProspect.title,
      description: n.vendorProspect.description,
      cta: n.vendorProspect.cta,
      actionType: 'createProspect',
    },
    n.vendorProspect
  );
}

export function resolveAdminNextAction({
  t,
  pendingGiftCount = 0,
  unreadNotifications = 0,
  hidePendingGifts = false,
}) {
  const n = t.ux?.nextBestAction;
  if (!n) return null;

  if (!hidePendingGifts && pendingGiftCount > 0) {
    return withMotivation(
      {
        id: 'review-gifts',
        icon: 'gift',
        title: n.adminGifts.title,
        description: n.adminGifts.description,
        cta: n.adminGifts.cta,
        meta: String(pendingGiftCount),
        actionType: 'navigate',
        target: 'pendingGifts',
      },
      n.adminGifts
    );
  }

  if (unreadNotifications > 0) {
    return withMotivation(
      {
        id: 'read-notifications',
        icon: 'bell',
        title: n.adminNotifications.title,
        description: n.adminNotifications.description,
        cta: n.adminNotifications.cta,
        meta: String(unreadNotifications),
        actionType: 'navigate',
        target: 'notifications',
      },
      n.adminNotifications
    );
  }

  return withMotivation(
    {
      id: 'review-pipeline',
      icon: 'chart',
      title: n.adminPipeline.title,
      description: n.adminPipeline.description,
      cta: n.adminPipeline.cta,
      actionType: 'navigate',
      target: 'users',
    },
    n.adminPipeline
  );
}
